import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Fragment, useState, useRef } from "react";
import { classNames } from "src/helpers/classNames";
import PreloaderIcon from "../shared/LoadingAnimations/PreloaderIcon";

const versions = {
  primary: {
    default: "text-white bg-highlightColor hover:shadow-md border-highlightColor",
    disabled: "bg-gray-50 text-gray-400 cursor-not-allowed border border-gray-200",
  },
  secondary: {
    default: "bg-white text-highlightColor border-[1px] border-highlightColor hover:shadow-sm",
    disabled: "bg-gray-50 text-gray-300 cursor-not-allowed border-400",
  },
  black: {
    default: "bg-white text-gray-700 border-gray-500 hover:border-gray-700 border-[1px] hover:border-highlightColor hover:shadow-sm hover:text-highlightColor",
    disabled: "cursor-not-allowed text-gray-400",
  },
  gray: {
    default: "bg-white text-gray-400 border-gray-200 hover:border-gray-400 border-[1px] hover:shadow-sm hover:text-gray-00",
    solid: "border-transparent bg-gray-50 text-gray-400 hover:bg-gray-100 hover:shadow-sm hover:text-gray-500",
    disabled: "cursor-not-allowed text-gray-200 border-gray-200/60",
  },
  default: {
    default: "",
    disabled: "cursor-not-allowed opacity-50",
  },
};

const ButtonDropdown = ({ xPosition = "left", dropdownOptions = [], children, version = "primary", color = "default", disabled = false, loading = false, style = {}, className = "", width = "", dropdownClasses = "", dragHandleProps = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (disabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  return (
    <Menu
      as="div"
      className="relative inline-block w-full text-left"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      {({ open }) => (
        <>
          <div>
            <Menu.Button
              {...dragHandleProps}
              disabled={disabled}
              style={style}
              className={classNames(
                "flex h-[47px] items-center",
                version !== "default" ? "group justify-center rounded-md border px-4 py-2 text-base font-medium transition-all duration-200 " : "",
                disabled ? versions[version].disabled : versions[version][color],
                width ? width : version !== "default" ? "w-full sm:w-auto" : "",
                className,
              )}>
              {version === "default" ? (
                children
              ) : (
                <div className="flex items-center gap-x-2 whitespace-nowrap">
                  {children}
                  {loading && (
                    <PreloaderIcon
                      height="h-5"
                      width="w-5"
                    />
                  )}
                  <ChevronDownIcon
                    className={classNames("h-4 w-4 transform transition-transform duration-200", isOpen ? "rotate-180" : "rotate-0")}
                    aria-hidden="true"
                  />
                </div>
              )}
            </Menu.Button>
          </div>

          {isOpen && <div className="absolute left-0 right-0 z-10 h-4 bg-transparent" />}

          <Transition
            show={isOpen}
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95">
            <Menu.Items
              static
              className={classNames("absolute z-50 mt-2 w-72 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none", xPosition === "right" ? "right-0" : "-top-36 left-0", dropdownClasses)}>
              <div className="divide-y py-2">
                {dropdownOptions.map((item, i) => (
                  <Menu.Item key={i}>
                    {({ active }) => (
                      <button
                        onClick={() => {
                          item?.onClick();
                          setIsOpen(false);
                        }}
                        className={`flex w-full items-center gap-x-3 px-4 py-2 text-left text-sm ${active ? "bg-gray-100" : ""}`}>
                        {item.icon && <span className="rounded-md border border-dashed border-highlightColor p-1.5 text-highlightColor">{item.icon}</span>}
                        <div className="flex flex-col justify-center pt-1">
                          <div className="font-semibold text-highlightColor">{item.title}</div>
                          <div className="text-xs text-gray-500">{item.description}</div>
                        </div>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default ButtonDropdown;
